import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CloudUpload, Loader2, RefreshCw, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const EcallAssignmentsPanel = ({ apiBase, isDark, textClass, labelClass, cardClass }) => {
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState({ total: 0, pushable: 0 });
  const [settings, setSettings] = useState({
    enabled: false,
    api_url: "",
    interval_minutes: 60,
    only_assigned: true,
    bearer_token_masked: "",
    last_success_at: null,
    last_error: null,
    last_saved: null,
  });
  const [tokenInput, setTokenInput] = useState("");
  const [showSettings, setShowSettings] = useState(false);

  const inputClass = isDark
    ? "bg-slate-950 border-slate-700 text-white"
    : "bg-white border-gray-200";

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [previewRes, settingsRes] = await Promise.all([
        axios.get(`${apiBase}/ecall-assignments`, { params: { action: "preview" } }),
        axios.get(`${apiBase}/ecall-assignments`, { params: { action: "settings" } }),
      ]);
      setStats({
        total: previewRes.data?.total ?? 0,
        pushable: previewRes.data?.pushable ?? 0,
      });
      setSettings(settingsRes.data?.settings || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || "eCall ayarları yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const body = {
        enabled: settings.enabled,
        api_url: settings.api_url,
        interval_minutes: settings.interval_minutes,
        only_assigned: settings.only_assigned,
      };
      if (tokenInput.trim()) {
        body.bearer_token = tokenInput.trim();
      }
      const res = await axios.put(`${apiBase}/ecall-assignments`, body);
      setSettings(res.data?.settings || settings);
      setTokenInput("");
      toast.success("Ayarlar kaydedildi");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  const pushNow = async () => {
    setPushing(true);
    try {
      const res = await axios.post(`${apiBase}/ecall-assignments`, { action: "push" }, { timeout: 300000 });
      const r = res.data?.result || {};
      if (r.ok) {
        toast.success(`Gönderildi: ${r.saved ?? 0} kayıt (toplam ${r.item_count ?? 0})`);
      } else {
        toast.error(r.errors?.[0] || "Gönderim başarısız");
      }
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.result?.errors?.[0] || "Gönderim hatası");
    } finally {
      setPushing(false);
    }
  };

  return (
    <Card className={cardClass}>
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className={`text-base font-semibold flex items-center gap-2 ${textClass}`}>
            <CloudUpload className="w-5 h-5 text-sky-500" />
            eCall Cihaz Atamaları
          </CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button type="button" size="sm" variant="outline" onClick={() => setShowSettings((v) => !v)} className={isDark ? "border-slate-700" : ""}>
              <Settings2 className="w-4 h-4 mr-1" />
              Ayarlar
            </Button>
            <Button type="button" size="sm" variant="outline" onClick={loadAll} disabled={loading} className={isDark ? "border-slate-700" : ""}>
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
            <Button type="button" size="sm" disabled={pushing || loading} onClick={pushNow} className="bg-sky-600 hover:bg-sky-500 text-white">
              {pushing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CloudUpload className="w-4 h-4 mr-1" />}
              Şimdi gönder
            </Button>
          </div>
        </div>
        <p className={`text-xs mt-1 ${labelClass}`}>
          Device ID, agent, team_lead, company (+ panelde GSM/firma detayı) → mobile.etelekom.net assignments API
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className={`text-sm flex flex-wrap gap-4 ${textClass}`}>
          <span>
            Kayıt: <strong>{loading ? "…" : stats.total}</strong>
          </span>
          <span>
            Gönderilebilir: <strong>{loading ? "…" : stats.pushable}</strong>
          </span>
          {settings.last_success_at && (
            <span className={labelClass}>
              Son başarı: {new Date(settings.last_success_at).toLocaleString("tr-TR")}
              {settings.last_saved != null ? ` (${settings.last_saved} kayıt)` : ""}
            </span>
          )}
          {settings.last_error && (
            <span className="text-rose-500 text-xs max-w-md truncate" title={settings.last_error}>
              Hata: {settings.last_error}
            </span>
          )}
        </div>

        {showSettings && (
          <div className={`rounded-lg border p-3 space-y-3 ${isDark ? "border-slate-800 bg-slate-950/40" : "border-gray-200 bg-gray-50/80"}`}>
            <div className="flex items-center justify-between gap-4">
              <label className={`text-sm ${textClass}`}>Otomatik gönderim (worker)</label>
              <Switch
                checked={!!settings.enabled}
                onCheckedChange={(v) => setSettings((s) => ({ ...s, enabled: v }))}
              />
            </div>
            <div>
              <label className={`text-xs ${labelClass}`}>API URL</label>
              <Input
                value={settings.api_url || ""}
                onChange={(e) => setSettings((s) => ({ ...s, api_url: e.target.value }))}
                className={`h-9 mt-1 text-xs ${inputClass}`}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={`text-xs ${labelClass}`}>Aralık (dakika, varsayılan 60)</label>
                <Input
                  type="number"
                  min={5}
                  max={1440}
                  value={settings.interval_minutes ?? 60}
                  onChange={(e) => setSettings((s) => ({ ...s, interval_minutes: Number(e.target.value) || 60 }))}
                  className={`h-9 mt-1 ${inputClass}`}
                />
              </div>
              <div className="flex items-end pb-1 gap-2">
                <Switch
                  checked={settings.only_assigned !== false}
                  onCheckedChange={(v) => setSettings((s) => ({ ...s, only_assigned: v }))}
                />
                <span className={`text-xs ${labelClass}`}>Yalnızca agent atanmış cihazlar</span>
              </div>
            </div>
            <div>
              <label className={`text-xs ${labelClass}`}>
                Bearer token {settings.bearer_token_masked ? `(kayıtlı: ${settings.bearer_token_masked})` : ""}
              </label>
              <Input
                type="password"
                value={tokenInput}
                onChange={(e) => setTokenInput(e.target.value)}
                placeholder="Yeni token (boş bırak = değiştirme)"
                className={`h-9 mt-1 font-mono text-xs ${inputClass}`}
                autoComplete="off"
              />
            </div>
            <Button type="button" size="sm" disabled={saving} onClick={saveSettings}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              Kaydet
            </Button>
            <p className={`text-[10px] ${labelClass}`}>
              Saatlik gönderim için sunucuda <code className="font-mono">dev-ecall-assignments-worker.bat</code> veya cron ile{" "}
              <code className="font-mono">ecall-assignments-worker.php</code> çalıştırın.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EcallAssignmentsPanel;
